import { Stack } from '@chakra-ui/react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DataTable } from '../../components/Table/DataTable';
import { dateTimeTransformer } from '../../helpers/dateTransformer';
import useScheduledPlanChangesQuery from '../../hooks/useScheduledPlanChangesQuery';
import { ContentCard } from '../../layout/ContentCard';

const ScheduledPlanChangesCard = (props: { workshopId: number }) => {
  const { workshopId } = props;
  const { t } = useTranslation();
  const scheduledPlanChangesStatusMapping = {
    100: 'pending',
    200: 'done',
    800: 'obsolete',
    900: 'failed',
  };

  const initialFilters = {
    itemsPerPage: 10,
  };

  const localStorageData = window.localStorage.getItem('scheduled_plan_changes_filters');
  // @ts-ignore

  const destringifiedData = JSON.parse(localStorageData) || {};
  useEffect(() => {
    if (!localStorageData) {
      window.localStorage.setItem('scheduled_plan_changes_filters', JSON.stringify(initialFilters));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sortOrder = 'asc';
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(destringifiedData?.itemsPerPage || initialFilters?.itemsPerPage);

  const initialLoad = itemsPerPage === 10 && page === 1 && sortOrder === 'asc';

  useEffect(() => {
    setPage(1);
  }, [sortOrder, itemsPerPage]);

  const scheduledPlanChangesQuery = useScheduledPlanChangesQuery({
    sortOrder,
    itemsPerPage,
    page,
    workshopId,
  });

  const currentPage = scheduledPlanChangesQuery.data?.meta?.current_page ?? 1;
  const lastPage = scheduledPlanChangesQuery?.data?.meta?.last_page ?? 1;

  const scheduledPlans = scheduledPlanChangesQuery.data?.data;
  const columns = useMemo(
    () => [
      {
        Header: t('workshop:status.label'),
        accessor: (row: { status: string }) => {
          return (
            <>
              {t(
                `workshop:scheduled_plan_changes.status.${
                  scheduledPlanChangesStatusMapping[row.status as unknown as keyof typeof scheduledPlanChangesStatusMapping]
                }`,
              )}
            </>
          );
        },
      },
      {
        Header: t('workshop:execution_date.label'),
        accessor: (row: { execution_date: string }) => {
          return <>{dateTimeTransformer(row.execution_date)}</>;
        },
      },
      {
        Header: t('workshop:takes_effect_at.label'),
        accessor: (row: { takes_effect_at: string }) => {
          return <>{dateTimeTransformer(row.takes_effect_at)}</>;
        },
      },
      {
        Header: t('workshop:plan_from.label'),
        accessor: (row: any) => {
          return row.new_plan.name;
        },
      },
      {
        Header: t('workshop:plan_to.label'),
        accessor: (row: any) => {
          return row.old_plan.name;
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [scheduledPlans],
  );

  return (
    <ContentCard
      isLoading={scheduledPlanChangesQuery.isLoading}
      minH="fit-content"
      header={t('workshop:scheduled_plan_changes.label')}
      data-test-id="scheduled-plan-changes-overview-card"
    >
      <Stack direction={['column', 'row']} w={'100%'}>
        <Stack direction="column" w="full">
          <DataTable
            data-test-id="scheduled-plan-changes-table"
            // @ts-ignore
            data={scheduledPlans || []}
            currentPage={currentPage - 1}
            total={scheduledPlanChangesQuery.data?.meta?.last_page}
            perPage={itemsPerPage}
            // @ts-ignore
            hasFilters={false}
            hasSearchInput={false}
            columns={columns}
            setPage={setPage}
            setPerPage={setItemsPerPage}
            loading={scheduledPlanChangesQuery.isLoading}
            // @ts-ignore
            isMoreThenOnePage={!initialLoad || Boolean(lastPage > 1)}
          />
        </Stack>
      </Stack>
    </ContentCard>
  );
};

export default ScheduledPlanChangesCard;
